import { useMemo } from 'react'
import { Package } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useListPackedItemsOverview,
  useListPackedItemsStats,
  type PackedItemsOverviewQuery,
  type PackedItemsOverviewSortBy,
  type PackedItemsOverviewSortDir,
} from '@/shared/hooks/api/packedItemsOverview'
import { useSearchParamsNavigate } from '@/shared/hooks/use-search-params-navigate'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { PackagedItemsOverviewTable } from './PackagedItemsOverviewTable'
import { PackedItemsFunnelStatsRow } from './PackedItemsFunnelStatsRow'
import { derivePackedItemsTabStatsFromStats } from './packedItemsOverviewStats'
import { PackagingSuppliersStatCardSkeleton } from './PackagingSuppliersStatCardSkeleton'

const PACKED_ITEMS_STAT_CARD_COUNT = 3

function parsePositiveInt(value: unknown, fallback: number): number {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  if (Number.isNaN(parsed) || parsed < 1) {
    return fallback
  }
  return parsed
}

const PACKED_ITEMS_SORT_BY_VALUES = [
  'name',
  'sku',
  'supplier',
  'complianceProgress',
  'audit',
] as const satisfies readonly PackedItemsOverviewSortBy[]

function parseSortBy(value: unknown): PackedItemsOverviewSortBy {
  const raw = typeof value === 'string' ? value.trim() : ''
  if (PACKED_ITEMS_SORT_BY_VALUES.includes(raw as PackedItemsOverviewSortBy)) {
    return raw as PackedItemsOverviewSortBy
  }
  return 'name'
}

function parseSortDir(value: unknown): PackedItemsOverviewSortDir {
  return value === 'desc' ? 'desc' : 'asc'
}

function buildPackedItemsQuery(
  search: Record<string, unknown>
): PackedItemsOverviewQuery {
  const page = parsePositiveInt(search.page, 1)
  const pageSize = Math.min(parsePositiveInt(search.pageSize, 20), 50)
  const searchQuery = typeof search.q === 'string' ? search.q.trim() : ''
  const sku = typeof search.sku === 'string' ? search.sku.trim() : ''
  const sortBy = parseSortBy(search.sortBy)
  const sortDir = parseSortDir(search.sortDir)

  return {
    page,
    pageSize,
    searchQuery: searchQuery === '' ? undefined : searchQuery,
    sku: sku === '' ? undefined : sku,
    sortBy,
    sortDir,
  }
}

export function PackedItemsPage() {
  const { t } = useTranslation('packagingSuppliers')
  const { search, navigate: searchNavigate } = useSearchParamsNavigate()
  const query = useMemo(() => buildPackedItemsQuery(search), [search])
  const exportQuery = useMemo(() => {
    const { page: _page, pageSize: _pageSize, ...filters } = query
    return filters
  }, [query])
  const { data: packedItemsOverview, isLoading, isFetching } =
    useListPackedItemsOverview(query)
  const { data: packedItemsStats, isLoading: isStatsLoading } =
    useListPackedItemsStats()
  const packedTabStats = useMemo(
    () => derivePackedItemsTabStatsFromStats(packedItemsStats),
    [packedItemsStats]
  )
  const items = packedItemsOverview?.items ?? []
  const pageInfo = packedItemsOverview?.pageInfo
  const totalPackedItems = packedItemsStats?.total ?? pageInfo?.totalCount ?? 0

  const showEmptyState =
    !isLoading && !isStatsLoading && totalPackedItems === 0
  const showStatsSkeleton = !showEmptyState && isStatsLoading
  const showStats = !showEmptyState && !isStatsLoading

  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main fixed className='flex min-h-0 flex-1 flex-col gap-4 px-6 sm:gap-6'>
        <div className='shrink-0'>
          <h2 className='text-2xl font-bold tracking-tight'>
            {t('pages.packedItems.title')}
          </h2>
          <p className='text-muted-foreground'>
            {t('pages.packedItems.description')}
          </p>
        </div>

        {showEmptyState && (
          <div className='flex flex-1 flex-col items-center justify-center gap-4 rounded-md border border-dashed py-16'>
            <Package className='size-10 text-muted-foreground/50' />
            <div className='space-y-1 text-center'>
              <h3 className='text-lg font-semibold'>
                {t('packedItems.emptyState.title')}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {t('packedItems.emptyState.description')}
              </p>
            </div>
          </div>
        )}

        {!showEmptyState && (
          <div className='flex min-h-0 flex-1 flex-col gap-4'>
            {showStatsSkeleton && (
              <div
                className='grid shrink-0 gap-2 sm:grid-cols-2 lg:grid-cols-3'
                aria-busy='true'
                aria-label={t('packedItems.loading')}
              >
                {Array.from({ length: PACKED_ITEMS_STAT_CARD_COUNT }, (_, index) => (
                  <PackagingSuppliersStatCardSkeleton key={index} />
                ))}
              </div>
            )}

            {showStats && (
              <PackedItemsFunnelStatsRow stats={packedTabStats.packedItems} />
            )}

            {isLoading && items.length === 0 && (
              <div className='shrink-0 py-12 text-center text-muted-foreground'>
                {t('packedItems.loading')}
              </div>
            )}

            {(items.length > 0 || !isLoading) && pageInfo != null && (
              <PackagedItemsOverviewTable
                items={items}
                pageInfo={pageInfo}
                exportQuery={exportQuery}
                isFetching={isFetching}
                search={search}
                navigate={searchNavigate}
                sortBy={query.sortBy ?? 'name'}
                sortDir={query.sortDir ?? 'asc'}
              />
            )}
          </div>
        )}
      </Main>
    </>
  )
}
